import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, AlertCircle, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { diagnosticsAPI, schedulerAPI, matchingAPI } from '../../lib/api';

const STATUS_ICON = {
  ok: <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />,
  warn: <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />,
  fail: <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />,
};

const STATUS_BADGE_VARIANT = {
  ok: 'default',
  warn: 'secondary',
  fail: 'destructive',
};

// Every matcher covered by the recurring-cost / statement-payout /
// auto-matching / statement-reconciliation scheduling audit. Kept as its
// own small, human-labeled list rather than deriving from the generic
// monitor checks so this card reads as "is matching running" at a glance,
// not another copy of the full system checklist.
const MATCHER_JOBS = [
  { checkId: 'scheduler_health_recurring_cost_matching', label: 'Recurring-cost matching', cadence: 'daily 06:30 UTC' },
  { checkId: 'scheduler_health_statement_payout_matching', label: 'Statement payout matching', cadence: 'daily 06:15 UTC' },
  { checkId: 'scheduler_health_statement_reconciliation_recompute', label: 'Statement reconciliation recompute', cadence: 'daily 06:45 UTC' },
  { checkId: 'scheduler_health_auto_matching', label: 'Invoice auto-matching (kicked off)', cadence: 'every 2h' },
  // Distinct from the row above: that one only proves a tick called
  // start_or_advance_auto_match_chain and got a response back (kickoff),
  // never that the chain run it started actually converged. This reads a
  // second, completion-time sync_logs signal
  // (routes/matching.py::_log_scheduler_run_outcome) -- see
  // INVOICE_AUTO_MATCH_SCHEDULER_DIAGNOSIS.md.
  { checkId: 'auto_matching_convergence', label: 'Invoice auto-matching (run outcome)', cadence: 'every 2h' },
];

// Live APScheduler + chain state for the "all" scope auto-match job,
// fetched straight from the existing scheduler/status and auto-match
// chain-status endpoints (no new backend surface) -- answers "is it even
// registered right now" and "is a run currently in flight" from the
// browser without ssh, per INVOICE_AUTO_MATCH_SCHEDULER_DIAGNOSIS.md B.3.
const AUTO_MATCH_SCHEDULER_JOB_ID = 'auto_matching';

function AutoMatchLiveState() {
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(undefined); // undefined = not fetched yet, null = not registered
  const [chain, setChain] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([schedulerAPI.getStatus(), matchingAPI.autoMatchChainStatus()])
      .then(([schedResp, chainResp]) => {
        if (cancelled) return;
        const jobs = schedResp.data?.jobs || [];
        setJob(jobs.find((j) => j.id === AUTO_MATCH_SCHEDULER_JOB_ID) || null);
        setChain(chainResp.data || null);
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to fetch live scheduler state'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="pl-5.5 ml-0.5 text-[11px] text-muted-foreground/80 mt-0.5">Loading live scheduler state...</div>;
  }
  if (error) {
    return <div className="pl-5.5 ml-0.5 text-[11px] text-red-500 mt-0.5">Live scheduler state: {error}</div>;
  }

  return (
    <div className="pl-5.5 ml-0.5 text-[11px] text-muted-foreground/80 mt-0.5">
      Live: registered={job ? 'yes' : 'no'}
      {job?.next_run && ` · next run ${new Date(job.next_run).toLocaleString()}`}
      {chain?.chain_status && ` · chain=${chain.chain_status}`}
      {chain?.computed_at && ` (as of ${new Date(chain.computed_at).toLocaleString()})`}
    </div>
  );
}

function MatcherRow({ job, check }) {
  const status = check ? check.status : 'fail';
  return (
    <div className="py-1.5 border-b last:border-b-0 border-border/50">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm min-w-0">
          {STATUS_ICON[status] || STATUS_ICON.fail}
          <span className="truncate">{job.label}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground">{job.cadence}</span>
          <Badge variant={STATUS_BADGE_VARIANT[status] || 'destructive'} className="text-xs">
            {status}
          </Badge>
        </div>
      </div>
      <div className="pl-5.5 ml-0.5 text-xs text-muted-foreground mt-0.5">
        {check ? check.value : 'check not found in monitor summary'}
      </div>
      {check?.detail && (
        <div className="pl-5.5 ml-0.5 text-[11px] text-muted-foreground/80 mt-0.5">{check.detail}</div>
      )}
      {job.checkId === 'scheduler_health_auto_matching' && <AutoMatchLiveState />}
    </div>
  );
}

export function MatchingScheduleCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    diagnosticsAPI.monitorSummary()
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to fetch monitor summary'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const checksById = new Map((data?.checks || []).map((c) => [c.id, c]));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-lg">Matching Schedule</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Every matcher runs on a schedule with its own run-now button; this shows whether the scheduled run is actually firing.
        </p>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
        {data && (
          <div>
            {MATCHER_JOBS.map((job) => (
              <MatcherRow key={job.checkId} job={job} check={checksById.get(job.checkId)} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
